import { findNearMatches, normaliseName } from "@corklog/shared";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { actionButton, control, fieldLabel, resting } from "./controls.js";

/**
 * A field that suggests without constraining.
 *
 * Two different things happen here and they must not be confused. The datalist
 * offers what this cellar has used before, so the common case is one keystroke
 * and a click. The prompt underneath asks *did you mean Bollinger?* when what
 * has been typed is nearly, but not exactly, something already on record.
 *
 * The prompt is offered and never applied. `Gosset` and `Gosset-Brabant` are
 * different Champagne houses, `Domaine Leflaive` and `Olivier Leflaive` are
 * different producers in the same village, and an algorithm that merged by
 * similarity would destroy distinctions like those silently over twenty years.
 * So the answer is decided by the person who knows it, at the moment of
 * writing, and dismissing the prompt is as easy as accepting it — because
 * sometimes the answer is genuinely no.
 *
 * Typing a value never used before is always allowed. The alternative is an
 * application that cannot record the first bottle from a new region, and a
 * cellar registering its very first wine has nothing to suggest at all.
 */
export function Picker({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  onEnter,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  placeholder?: string;
  /**
   * What Return means in this field, where it means something of its own.
   *
   * A picker that feeds a list — the grapes — has its own idea of Return: add
   * what has been typed. Without this the browser's idea wins, which is to
   * submit the form, and the wine is saved without the word still standing in
   * the field. Left undefined elsewhere, where submitting is the right answer.
   */
  onEnter?: () => void;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const [dismissed, setDismissed] = useState<string[]>([]);

  // Nothing to ask about until something has been typed, and nothing to ask
  // about once it matches a row exactly — that is the row, not a near miss.
  const typed = value.trim();
  const exact = options.some((option) => normaliseName(option) === normaliseName(typed));
  const suggestions =
    typed === "" || exact
      ? []
      : findNearMatches(typed, options).filter(
          (match) => !dismissed.includes(match.value),
        );

  return (
    <div>
      <label className={fieldLabel} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        list={listId}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (onEnter === undefined || event.key !== "Enter") return;
          event.preventDefault();
          onEnter();
        }}
        className={`${control} ${resting} mt-1`}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>

      {suggestions.length > 0 && (
        <p className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
          <span className="text-muted">{t("picker.didYouMean")}</span>
          {suggestions.slice(0, 3).map((match) => (
            <span key={match.value} className="flex items-baseline gap-1">
              <button
                type="button"
                onClick={() => onChange(match.value)}
                className="text-accent underline underline-offset-4"
              >
                {match.value}
              </button>
              <button
                type="button"
                aria-label={t("picker.dismiss", { name: match.value })}
                onClick={() => setDismissed((current) => [...current, match.value])}
                className="text-muted hover:text-ink px-1"
              >
                ×
              </button>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/**
 * One grape onto a list, or the same list back when it is already there.
 *
 * Two spellings of one grape in a blend are one grape. The rule lives here and
 * not in the component, because the form needs it too: answering *add it and
 * save* has to produce exactly the list the button would have produced, and a
 * second copy of "is it already there" is a second answer waiting to differ.
 */
export function addGrape(values: readonly string[], name: string): string[] {
  const trimmed = name.trim();
  if (trimmed === "") return [...values];
  const already = values.some((value) => normaliseName(value) === normaliseName(trimmed));
  return already ? [...values] : [...values, trimmed];
}

/**
 * The grapes of a wine, as a list rather than a text field holding commas.
 * A blend is normal, so this is genuinely several values — and each one goes
 * through the same picker, so `nebbiolo` finds `Nebbiolo` before a second row
 * for it can exist.
 *
 * **The half-typed grape belongs to the form, not to this component.** A word
 * standing in the field is not on the wine, and the form is what decides to
 * save; if this component kept the draft to itself, the only two possible
 * behaviours would be to lose it silently or to add it without being asked.
 * Held here, the form can ask — see `WineForm`.
 */
export function GrapePicker({
  values,
  onChange,
  options,
  draft,
  onDraftChange,
}: {
  values: readonly string[];
  onChange: (values: string[]) => void;
  options: readonly string[];
  /** What has been typed and not yet added. Owned by the form. */
  draft: string;
  onDraftChange: (draft: string) => void;
}) {
  const { t } = useTranslation();

  const add = () => {
    if (draft.trim() === "") return;
    onChange(addGrape(values, draft));
    onDraftChange("");
  };

  // Label, then the field, then what has been added — in that order, so the
  // label reads as belonging to the input rather than to the chips above it.
  return (
    <div>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Picker
            id="wine-grape-draft"
            label={t("wines.grapes")}
            value={draft}
            onChange={onDraftChange}
            options={options}
            placeholder={t("wines.grapesHint")}
            onEnter={add}
          />
        </div>
        {/* Beside the field and shaped like it, rather than a word floating at
            the far edge of the row. It is the second half of one action, and
            the distance between them was most of the reason the first half
            could be mistaken for the whole of it. */}
        <button
          type="button"
          onClick={add}
          disabled={draft.trim() === ""}
          className={actionButton}
        >
          {t("picker.addGrape")}
        </button>
      </div>

      {values.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {values.map((grape) => (
            <li
              key={grape}
              className="border-line flex items-baseline gap-2 border px-2 py-0.5 text-sm"
            >
              <span>{grape}</span>
              <button
                type="button"
                aria-label={t("picker.remove", { name: grape })}
                onClick={() => onChange(values.filter((value) => value !== grape))}
                className="text-muted hover:text-past"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
